import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { siPuoUscire } from '../../utils/uscita';
import authStore from '../../store/authStore';
import {
  Home, Users, FileText, Bus, CreditCard, UserCircle,
  BarChart2, Settings, Search, Bell, LogOut, Menu, X, Eye, History, FileSignature, Grid3x3,
  Ticket, Euro, Copy
} from 'lucide-react';

// Parco mezzi e report restano riservati agli amministratori
const SOLO_ADMIN = ['/admin/pullman', '/admin/report'];

/**
 * Il menu dipende dall'area: tenere insieme trasporto scolastico,
 * preventivi ed eventi produrrebbe un elenco illeggibile.
 *
 * Dentro l'area le voci sono raggruppate. Il trasporto scolastico ne ha
 * nove, e un elenco piatto di nove bottoni non si legge: chi cerca i
 * pagamenti li trova fra i clienti, chi cerca lo storico fra le corse.
 * Una sezione senza titolo sta in cima senza intestazione.
 */
const MENU_PER_AREA = {
  scolastico: {
    titolo: 'Trasporto Scolastico',
    sezioni: [
      {
        voci: [
          { id: '/admin/dashboard', label: 'Dashboard', icon: Home, path: '/admin/dashboard' },
        ],
      },
      {
        titolo: 'Abbonati',
        voci: [
          { id: '/admin/clienti', label: 'Clienti', icon: Users, path: '/admin/clienti' },
          { id: '/admin/abbonamenti', label: 'Abbonamenti', icon: FileText, path: '/admin/abbonamenti' },
          { id: '/admin/pagamenti', label: 'Pagamenti', icon: CreditCard, path: '/admin/pagamenti' },
        ],
      },
      {
        titolo: 'Servizio',
        voci: [
          { id: '/admin/linee', label: 'Gruppi e Corse', icon: Bus, path: '/admin/linee' },
          { id: '/admin/storico-corse', label: 'Storico Corse', icon: History, path: '/admin/storico-corse' },
          { id: '/admin/tesserini-condivisi', label: 'Tesserini condivisi', icon: Copy, path: '/admin/tesserini-condivisi' },
        ],
      },
      {
        titolo: 'Azienda',
        voci: [
          { id: '/admin/report', label: 'Report', icon: BarChart2, path: '/admin/report' },
          { id: '/admin/impostazioni', label: 'Impostazioni', icon: Settings, path: '/admin/impostazioni' },
        ],
      },
    ],
  },
  // Pullman e autisti stanno qui e non nel trasporto scolastico: gli
  // stessi mezzi e gli stessi autisti fanno le corse della scuola, le
  // gite a noleggio e le navette degli eventi.
  generale: {
    titolo: 'Generale',
    sezioni: [
      {
        titolo: 'Noleggi',
        voci: [
          { id: '/admin/preventivi', label: 'Preventivi', icon: FileSignature, path: '/admin/preventivi' },
          { id: '/admin/committenti', label: 'Committenti', icon: Users, path: '/admin/committenti' },
        ],
      },
      {
        titolo: 'Mezzi e autisti',
        voci: [
          { id: '/admin/pullman', label: 'Pullman', icon: Bus, path: '/admin/pullman' },
          { id: '/admin/autisti', label: 'Autisti', icon: UserCircle, path: '/admin/autisti' },
        ],
      },
    ],
  },
  eventi: {
    titolo: 'Navette Eventi',
    sezioni: [
      {
        titolo: 'Vendita',
        voci: [
          { id: '/admin/eventi', label: 'Eventi', icon: Ticket, path: '/admin/eventi' },
          { id: '/admin/eventi/ordini', label: 'Ordini', icon: Euro, path: '/admin/eventi/ordini' },
          // Anagrafica a se': chi compra un biglietto non e' uno studente
          // abbonato ne' un committente di noleggi.
          { id: '/admin/eventi/clienti', label: 'Clienti', icon: Users, path: '/admin/eventi/clienti' },
        ],
      },
      {
        titolo: 'Configurazione',
        voci: [
          { id: '/admin/eventi/impostazioni', label: 'Impostazioni', icon: Settings, path: '/admin/eventi/impostazioni' },
        ],
      },
    ],
  },
};

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { utente, logout } = authStore();
  // Su schermi stretti la barra laterale è a scomparsa: occuperebbe
  // 256px dei ~375 disponibili su un telefono.
  const [menuAperto, setMenuAperto] = useState(false);

  const PERCORSI_GENERALE = [
    '/admin/preventivi', '/admin/committenti', '/admin/autisti', '/admin/pullman',
  ];
  const areaCorrente = location.pathname.startsWith('/admin/eventi')
    ? 'eventi'
    : PERCORSI_GENERALE.some((v) => location.pathname.startsWith(v))
      ? 'generale'
      : 'scolastico';
  const area = MENU_PER_AREA[areaCorrente];

  // Le sezioni rimaste vuote dopo il filtro non devono lasciare in
  // giro la loro intestazione.
  const solaLettura = utente?.ruolo === 'SOLA_LETTURA';
  const sezioni = area.sezioni
    .map((sezione) => ({
      ...sezione,
      voci: solaLettura
        ? sezione.voci.filter((v) => !SOLO_ADMIN.includes(v.path))
        : sezione.voci,
    }))
    .filter((sezione) => sezione.voci.length > 0);

  // Cambiando pagina il menu si richiude, altrimenti resterebbe
  // sopra il contenuto appena aperto
  useEffect(() => {
    setMenuAperto(false);
  }, [location.pathname]);

  const handleLogout = () => {
    if (!siPuoUscire()) return;
    logout();
    navigate('/login');
  };

  const handleMenuClick = (path) => {
    // Una schermata con modifiche non salvate puo' chiedere di
    // fermarsi: senza questo si perde tutto cambiando voce di menu.
    if (!siPuoUscire()) return;
    navigate(path);
  };

  const iniziale = utente?.first_name?.[0] || utente?.username?.[0] || 'A';

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Velo scuro dietro al menu aperto, solo su schermi stretti */}
      {menuAperto && (
        <div
          onClick={() => setMenuAperto(false)}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          aria-hidden="true"
        />
      )}

      {/* Barra laterale: fissa da lg in su, a scomparsa sotto.
          Colonna in tre pezzi - intestazione e piede fermi, il menu che
          scorre in mezzo. Il piede stava in `absolute` dentro un
          contenitore che scorre, e con dieci voci finiva sopra le
          ultime. */}
      <aside
        className={`w-64 flex-shrink-0 z-50 flex flex-col transition-transform duration-200
          fixed inset-y-0 left-0 lg:static lg:translate-x-0
          ${menuAperto ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #1b94ce 0%, #e62494 100%)' }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/20 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Bus className="w-10 h-10 text-white flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-white font-bold text-lg truncate">Casella</h1>
              {/* L'area in cui si sta lavorando, non più solo il nome azienda */}
              <p className="text-white/80 text-sm truncate">{area.titolo}</p>
            </div>
          </div>
          <button
            onClick={() => setMenuAperto(false)}
            className="lg:hidden p-1 text-white/80 hover:text-white"
            aria-label="Chiudi menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Menu: `min-h-0` serve, altrimenti flex non lo lascia
            rimpicciolire sotto il suo contenuto e non scorre. */}
        <nav className="flex-1 min-h-0 overflow-y-auto mt-4 px-4 pb-4">
          <button
            onClick={() => { if (!siPuoUscire()) return; setMenuAperto(false); navigate('/admin'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 mb-2 rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition text-sm font-medium"
          >
            <Grid3x3 className="w-5 h-5 flex-shrink-0" />
            <span>Cambia area</span>
          </button>

          {sezioni.map((sezione, indice) => (
            <div key={sezione.titolo || `sezione-${indice}`} className="mt-3">
              {sezione.titolo && (
                <p className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/50">
                  {sezione.titolo}
                </p>
              )}
              {sezione.voci.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item.path)}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg mb-1 transition-all ${
                      isActive
                        ? 'bg-white text-[#1b94ce] shadow-lg'
                        : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium text-left">{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 px-6 py-4 border-t border-white/15">
          <div className="text-center text-white/60 text-xs">
            Powered by <span className="font-semibold text-white/80">Neoflux</span>
          </div>
        </div>
      </aside>

      {/* Contenuto */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-16 flex items-center justify-between gap-3 px-4 lg:px-8 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3 flex-1 min-w-0 max-w-2xl">
            <button
              onClick={() => setMenuAperto(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition flex-shrink-0"
              aria-label="Apri menu"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>

            {/* La ricerca occupa troppo spazio su telefono: compare da sm in su */}
            <div className="relative w-full hidden sm:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca..."
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1b94ce] focus:border-transparent"
              />
            </div>

            <span className="sm:hidden font-bold text-gray-900 truncate">Casella</span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-[#e62494] rounded-full"></span>
            </button>

            <div className="flex items-center space-x-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #1b94ce 0%, #e62494 100%)' }}
              >
                <span className="text-white font-semibold">{iniziale}</span>
              </div>
              {/* Nome e ruolo si nascondono su telefono */}
              <div className="text-left hidden md:block">
                <p className="text-gray-900 text-sm font-medium">
                  {utente?.first_name || utente?.username || 'Admin'}
                </p>
                <p className="text-gray-500 text-xs">
                  {utente?.ruolo === 'SOLA_LETTURA' ? 'Consultazione' : 'Amministratore'}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-lg transition flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #1b94ce 0%, #e62494 100%)' }}
              aria-label="Esci"
            >
              <LogOut className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium hidden sm:inline">Esci</span>
            </button>
          </div>
        </header>

        {/* Chi consulta soltanto deve saperlo, altrimenti i comandi
            mancanti sembrano un guasto */}
        {utente?.ruolo === 'SOLA_LETTURA' && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-8 py-2 flex items-center gap-2">
            <Eye className="w-4 h-4 text-amber-700 shrink-0" />
            <p className="text-xs text-amber-900">
              Accesso in <strong>sola consultazione</strong>: puoi vedere tutti i dati ma non modificarli.
            </p>
          </div>
        )}

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
